import React, { useState } from 'react';
import { 
  useSavedSolicitations, 
  SavedSolicitation 
} from '../SavedSolicitationsContext';
import { 
  Calendar as CalendarIcon, 
  Bell, 
  Clock, 
  Trash2, 
  ExternalLink, 
  ChevronLeft, 
  ChevronRight, 
  AlertCircle,
  CheckCircle,
  HelpCircle,
  Star,
  Search
} from 'lucide-react';

export const RemindersView: React.FC = () => {
  const { 
    savedList, 
    loading, 
    toggleSave, 
    updateReminderPreference, 
    checkDeadlines 
  } = useSavedSolicitations();

  // Tab view state
  const [remindersTab, setRemindersTab] = useState<'matrix' | 'calendar' | 'settings'>('matrix');
  const [searchQuery, setSearchQuery] = useState('');

  // Calendar view states
  const [currentDate, setCurrentDate] = useState(new Date(2026, 5, 11)); // Default to June 2026 based on mock session
  const [selectedDay, setSelectedDay] = useState<number | null>(null);

  // Quick state lists
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Calculate days in the current month
  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Get the first weekday of the month
  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month, 1).getDay();
  };

  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDayIndex = getFirstDayOfMonth(currentYear, currentMonth);

  // Navigation handlers
  const handlePrevMonth = () => {
    setCurrentDate(prev => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() - 1);
      return d;
    });
    setSelectedDay(null);
  };

  const handleNextMonth = () => {
    setCurrentDate(prev => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() + 1);
      return d;
    });
    setSelectedDay(null);
  };

  // Get solicitations due on a particular date
  const getSolicitationsDueOn = (year: number, month: number, day: number): SavedSolicitation[] => {
    return savedList.filter(item => {
      if (!item.dueDate) return false;
      const due = new Date(item.dueDate);
      return due.getFullYear() === year && due.getMonth() === month && due.getDate() === day;
    });
  };

  // Calculate days remaining helper
  const getDaysRemainingCount = (dueDateStr: string): number => {
    if (!dueDateStr) return Infinity;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDateStr);
    due.setHours(0, 0, 0, 0);
    const diffTime = due.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getUrgencyClasses = (days: number) => {
    if (days < 0) return {
      badge: 'bg-gray-100 text-gray-700 border-gray-200',
      text: 'text-gray-500',
      label: 'Closed / Passed',
      dotColor: 'bg-gray-400'
    };
    if (days <= 1) return {
      badge: 'bg-rose-50 border border-rose-100 text-rose-700 font-bold',
      text: 'text-rose-600 font-bold',
      label: '⚠️ Urgent: Due Tomorrow',
      dotColor: 'bg-rose-500 animate-pulse'
    };
    if (days <= 3) return {
      badge: 'bg-amber-50 border border-amber-100 text-amber-700 font-bold',
      text: 'text-amber-600 font-medium',
      label: '⏳ Due in <= 3 days',
      dotColor: 'bg-amber-500'
    };
    if (days <= 7) return {
      badge: 'bg-yellow-50 border border-yellow-100 text-yellow-800',
      text: 'text-yellow-700',
      label: 'Upcoming: Due in 1 week',
      dotColor: 'bg-yellow-400'
    };
    return {
      badge: 'bg-emerald-50 border border-emerald-100 text-emerald-700',
      text: 'text-emerald-600',
      label: 'On Track',
      dotColor: 'bg-emerald-500'
    };
  };

  // Days array for grid layout
  const calendarCells = [];
  // Empty slots for preceding month days offsets
  for (let i = 0; i < firstDayIndex; i++) {
    calendarCells.push(null);
  }
  // Current month days
  for (let day = 1; day <= daysInMonth; day++) {
    calendarCells.push(day);
  }

  // Filter list by selected calendar day and search
  const filteredList = savedList.filter(item => {
    const matchesDay = selectedDay 
      ? getSolicitationsDueOn(currentYear, currentMonth, selectedDay).some(s => s.id === item.id)
      : true;
    const matchesSearch = searchQuery 
      ? item.title?.toLowerCase().includes(searchQuery.toLowerCase()) || 
        item.agency?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.solicitationId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.state?.toLowerCase().includes(searchQuery.toLowerCase())
      : true;
    return matchesDay && matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Header and Subtab Navigation Bar */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CalendarIcon className="w-6 h-6 text-indigo-600" />
            Deadlines &amp; Watchlist Portal
          </h1>
          <p className="text-sm text-gray-500">
            Track approaching bid proposal deadlines, schedule automatic reminders, and monitor deadline matrix in a single view.
          </p>
        </div>

        {/* Tab Controls */}
        <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-xl">
          <button
            onClick={() => setRemindersTab('matrix')}
            className={`px-4 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
              remindersTab === 'matrix' ? 'bg-white text-indigo-700 shadow-xs' : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            Watchlist Matrix ({savedList.length})
          </button>
          <button
            onClick={() => setRemindersTab('calendar')}
            className={`px-4 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
              remindersTab === 'calendar' ? 'bg-white text-indigo-700 shadow-xs' : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            <CalendarIcon className="w-3.5 h-3.5" />
            Calendar Grid
          </button>
          <button
            onClick={() => setRemindersTab('settings')}
            className={`px-4 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
              remindersTab === 'settings' ? 'bg-white text-indigo-700 shadow-xs' : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            Alert Settings
          </button>
        </div>
      </div>

      {/* Tab 1: High Density Tabular Watchlist Matrix */}
      {remindersTab === 'matrix' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-white p-4 rounded-2xl border border-gray-200">
            <div className="relative flex-1 w-full">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search watchlist by RFX ID, title, agency, or state..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-gray-200 rounded-xl text-xs outline-none focus:bg-white focus:ring-2 focus:ring-indigo-500/20"
              />
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-end">
              <span className="text-xs text-gray-500 font-medium whitespace-nowrap">
                {filteredList.length} Opportunities Tracked
              </span>
              <button 
                onClick={checkDeadlines}
                className="inline-flex items-center gap-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-bold px-3 py-2 rounded-xl transition-colors cursor-pointer border border-indigo-200"
              >
                <Bell className="w-3.5 h-3.5" />
                Scan Alerts Now
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 bg-white border border-gray-200 rounded-2xl">
              <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mb-3" />
              <p className="text-sm font-semibold text-gray-500">Loading saved opportunities...</p>
            </div>
          ) : filteredList.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center space-y-4">
              <div className="w-16 h-16 bg-gray-50 rounded-full mx-auto flex items-center justify-center">
                <Bell className="w-8 h-8 text-gray-300" />
              </div>
              <div className="space-y-1 max-w-sm mx-auto">
                <h3 className="font-bold text-gray-900 text-lg">No Watchlist Items</h3>
                <p className="text-xs text-gray-500">
                  {searchQuery ? "No tracked solicitations match your search filter." : "Add solicitations to your watch list using the star/bookmark icon in the Solicitations tab to track approaching deadlines."}
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-xs">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-slate-50/80 border-b border-gray-200 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                      <th className="py-3 px-4">RFX Opportunity</th>
                      <th className="py-3 px-3">State</th>
                      <th className="py-3 px-3">Due Date</th>
                      <th className="py-3 px-3">Urgency Status</th>
                      <th className="py-3 px-3">Reminder Notification</th>
                      <th className="py-3 px-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredList.map((item) => {
                      const daysRem = getDaysRemainingCount(item.dueDate);
                      const urgency = getUrgencyClasses(daysRem);
                      return (
                        <tr key={item.id} className="hover:bg-indigo-50/30 transition-colors">
                          <td className="py-3 px-4">
                            <div className="min-w-0">
                              <p className="font-bold text-slate-900 truncate max-w-md" title={item.title}>
                                {item.title}
                              </p>
                              <div className="flex items-center gap-2 mt-0.5 text-[11px] text-slate-400">
                                <span className="font-mono font-bold text-indigo-700 bg-indigo-50 px-1.5 py-0.2 rounded border border-indigo-100">
                                  {item.solicitationId}
                                </span>
                                <span className="truncate max-w-xs">{item.agency}</span>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-3">
                            <span className="font-bold text-slate-700 bg-slate-100 px-2 py-0.5 rounded text-[10px]">
                              {item.state}
                            </span>
                          </td>
                          <td className="py-3 px-3 font-medium text-slate-800">
                            {item.dueDate ? new Date(item.dueDate).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="py-3 px-3">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${urgency.badge}`}>
                              {daysRem < 0 ? 'Passed' : daysRem === 0 ? 'TODAY' : daysRem === 1 ? '1 Day Left' : `${daysRem} Days Left`}
                            </span>
                          </td>
                          <td className="py-3 px-3">
                            <select
                              className="text-xs border border-gray-200 outline-none rounded-lg bg-white p-1.5 font-medium focus:ring-2 focus:ring-indigo-500/10 cursor-pointer"
                              value={item.reminderDaysBefore}
                              onChange={(e) => updateReminderPreference(item.solicitationId, Number(e.target.value))}
                            >
                              <option value={1}>1 Day Before</option>
                              <option value={3}>3 Days Before</option>
                              <option value={5}>5 Days Before</option>
                              <option value={7}>7 Days Before</option>
                              <option value={0}>Disabled</option>
                            </select>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                type="button"
                                onClick={() => toggleSave(item)}
                                className="p-1.5 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                                title="Remove from watchlist"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab 2: Monthly Calendar Grid */}
      {remindersTab === 'calendar' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5 space-y-4">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="font-bold text-gray-900 tracking-tight flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4 text-indigo-600" />
                  {monthNames[currentMonth]} {currentYear}
                </h2>
                <div className="flex items-center gap-1">
                  <button
                    onClick={handlePrevMonth}
                    className="p-1.5 hover:bg-gray-100 border border-gray-100 rounded-lg text-gray-500 hover:text-gray-800 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setCurrentDate(new Date())}
                    className="px-2.5 py-1 text-xs font-semibold hover:bg-gray-150 rounded-lg text-indigo-700 transition-colors border border-gray-100"
                  >
                    Today
                  </button>
                  <button
                    onClick={handleNextMonth}
                    className="p-1.5 hover:bg-gray-100 border border-gray-100 rounded-lg text-gray-500 hover:text-gray-800 transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-7 gap-1 text-center mb-3">
                {daysOfWeek.map(day => (
                  <span key={day} className="text-xs font-bold text-gray-400 uppercase tracking-wider py-1">
                    {day}
                  </span>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1.5">
                {calendarCells.map((day, idx) => {
                  if (day === null) {
                    return <div key={`empty-${idx}`} className="aspect-square bg-gray-50/50 rounded-lg" />;
                  }

                  const dues = getSolicitationsDueOn(currentYear, currentMonth, day);
                  const hasDues = dues.length > 0;
                  
                  let maxUrgencyColor = 'bg-gray-400';
                  if (hasDues) {
                    const minDays = Math.min(...dues.map(d => getDaysRemainingCount(d.dueDate)));
                    const config = getUrgencyClasses(minDays);
                    maxUrgencyColor = config.dotColor;
                  }

                  const isSelected = selectedDay === day;
                  const isToday = new Date().getFullYear() === currentYear && 
                                  new Date().getMonth() === currentMonth && 
                                  new Date().getDate() === day;

                  return (
                    <button
                      key={`day-${day}`}
                      onClick={() => setSelectedDay(isSelected ? null : day)}
                      className={`aspect-square rounded-xl p-1 shrink-0 flex flex-col justify-between items-center transition-all relative border cursor-pointer group ${
                        isSelected 
                          ? 'border-indigo-600 bg-indigo-50/30 font-bold' 
                          : isToday 
                            ? 'border-indigo-600 text-indigo-600 font-bold bg-indigo-50/10'
                            : 'border-transparent hover:border-gray-200 hover:bg-gray-50 text-gray-800'
                      }`}
                    >
                      <span className="text-xs">{day}</span>
                      <div className="flex gap-1 items-center pb-1">
                        {hasDues && (
                          <span className={`w-2 h-2 rounded-full ${maxUrgencyColor}`} title={`${dues.length} deadline(s) due`} />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {selectedDay !== null && (
                <div className="mt-4 p-3 bg-indigo-50/40 border border-indigo-100 rounded-xl flex justify-between items-center">
                  <span className="text-xs text-indigo-900 font-medium">
                    Showing deadlines for <strong>{monthNames[currentMonth]} {selectedDay}, {currentYear}</strong>
                  </span>
                  <button
                    onClick={() => setSelectedDay(null)}
                    className="text-xs text-indigo-600 hover:underline font-semibold cursor-pointer"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-7 space-y-4">
            <h2 className="text-base font-bold text-gray-900">
              {selectedDay !== null ? `Deadlines Due on ${monthNames[currentMonth]} ${selectedDay} (${filteredList.length})` : `All Scheduled Deadlines (${savedList.length})`}
            </h2>
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-xs">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-50/80 border-b border-gray-200 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                    <th className="py-3 px-4">Opportunity</th>
                    <th className="py-3 px-3">State</th>
                    <th className="py-3 px-3">Due Date</th>
                    <th className="py-3 px-3">Alert Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredList.map((item) => {
                    const daysRem = getDaysRemainingCount(item.dueDate);
                    const urgency = getUrgencyClasses(daysRem);
                    return (
                      <tr key={item.id} className="hover:bg-slate-50">
                        <td className="py-3 px-4">
                          <p className="font-bold text-slate-900 truncate max-w-sm">{item.title}</p>
                          <p className="text-[11px] text-slate-500">{item.solicitationId} &bull; {item.agency}</p>
                        </td>
                        <td className="py-3 px-3 font-semibold text-slate-700">{item.state}</td>
                        <td className="py-3 px-3 font-medium text-slate-800">
                          {item.dueDate ? new Date(item.dueDate).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="py-3 px-3">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${urgency.badge}`}>
                            {daysRem < 0 ? 'Passed' : daysRem === 0 ? 'TODAY' : `${daysRem}d Left`}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Tab 3: Notification Settings */}
      {remindersTab === 'settings' && (
        <div className="bg-white p-6 rounded-2xl border border-gray-200 space-y-4 max-w-2xl">
          <h3 className="font-bold text-base text-gray-900">Deadline Notification Settings</h3>
          <p className="text-xs text-gray-500 leading-relaxed">
            Configure how RFXpress delivers countdown notifications and deadline alerts. Alerts scan active bids automatically on session startup.
          </p>
          <div className="pt-2 space-y-3">
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between">
              <div>
                <p className="text-xs font-bold text-slate-900">Default Notification Window</p>
                <p className="text-[11px] text-slate-500">Standard lead time for new watchlist additions</p>
              </div>
              <span className="font-bold text-xs text-indigo-700 bg-indigo-50 px-3 py-1 rounded-lg border border-indigo-200">
                3 Days Before
              </span>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between">
              <div>
                <p className="text-xs font-bold text-slate-900">In-App Toast Banner Signals</p>
                <p className="text-[11px] text-slate-500">Display persistent top notification badges for critical expiring bids</p>
              </div>
              <span className="font-bold text-xs text-emerald-700 bg-emerald-50 px-3 py-1 rounded-lg border border-emerald-200">
                Enabled
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default RemindersView;
